import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { isGlobalViewsWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import addProductPage from "@tSurya/Handlers/ProductList/AddProductPage";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
    addProductPage,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    select?: SelectPresentationProps;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    select: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 325px;

                @media (max-width: 375px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = sortSelectStyles;

// MGBW sort options: Best Match (1), Featured (23), New (21), Top Sellers (22), Name: A to Z (9), Name: Z to A (10).
const mgbwAllowedSortTypes = ["1", "23", "21", "22", "9", "10"];

const ProductListSortSelect = ({ addProductFilters, pagination, addProductPage }: Props) => {
    if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductPage({ sort: event.currentTarget.value, page: 1 });
    };

    const isGlobalViews = isGlobalViewsWebsite();
    const isMgbw = isMgbwWebsite();

    return (
        <StyledWrapper
            css={css`
                width: auto;
                justify-self: start;
                text-align: left;
                max-width: 100%;

                ${({ theme }) =>
                    isGlobalViewsWebsite()
                        ? breakpointMediaQueries(
                              theme,
                              [
                                  null,
                                  css`
                                      margin-right: 20px;
                                  `,
                                  null,
                                  null,
                                  null,
                              ],
                              "max",
                          )
                        : ""}

                [data-test-selector="productListSortSelect-label"] {
                    white-space: nowrap !important;
                    margin: 0 !important;
                    flex: 0 0 auto !important;
                    width: auto !important;
                }

                > [class^="FormFieldStyle"] {
                    display: inline-flex !important;
                    align-items: center !important;
                    flex-wrap: nowrap !important;
                    gap: 8px !important;
                    max-width: 100% !important;
                    justify-content: flex-start !important;
                }

                [data-test-selector="productListSortSelect-label"] + [class^="FormInputWrapper"] {
                    flex: 1 1 auto !important;
                    min-width: 0 !important;
                }

                [data-test-selector="productListSortSelect-label"] + [class^="FormInputWrapper"] [data-test-selector="productListSortSelect"] {
                    width: 100% !important;
                    min-width: 0 !important;
                }
            `}
        >
            <Select
                {...styles.select}
                label={translate(isMgbwWebsite() ? "Sort By:" : "Sort by")}
                value={pagination.sortType}
                labelPosition="left"
                preventEllipsis={true}
                onChange={onChangeSortHandler}
                data-test-selector="productListSortSelect"
            >
                {pagination.sortOptions
                    .filter(sortOpt => {
                        if (isMgbw) {
                            return mgbwAllowedSortTypes.indexOf(sortOpt.sortType) !== -1;
                        }

                        if (isGlobalViews && sortOpt.sortType === "22") {
                            return false;
                        }

                        return true;
                    })
                    ?.map(sortOption => (
                        <option key={sortOption.sortType} value={sortOption.sortType}>
                            {sortOption.displayName}
                        </option>
                    ))}
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "Sort Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
